import Button from "@mui/material/Button";
import { SyntheticEvent, useEffect, useState } from "react";
import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { useMenu } from "../hooks/useMenu";
import { useCart } from "../hooks/useCart";
import { DndContext, DragEndEvent, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates
} from "@dnd-kit/sortable";
import { Droppable } from "./sortable/Droppable";
import { IMeal, WeekDaysEnum } from "../types/types";
import { WeekdayOverlay } from "./sortable/WeekdayOverlay";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { FloatingButton } from "react-draggable-float-btn";
import AddIcon from '@mui/icons-material/Add';
import { MealListModal } from "./MealListModal";


export const Menu = () => {
  const { menu, setMenu } = useMenu();
  const { cart } = useCart();
  const breakfast = menu.filter(({ type }) => type === "breakfast").map((meal) => ({ ...meal, id: meal.id! }));
  const lunch = menu.filter(({ type }) => type === "lunch").map((meal) => ({ ...meal, id: meal.id! }));
  const dinner = menu.filter(({ type }) => type === "dinner").map((meal) => ({ ...meal, id: meal.id! }));
  const snack = menu.filter(({ type }) => type === "snack").map((meal) => ({ ...meal, id: meal.id! }));
  const adultDinner = dinner.filter((meal) => !meal.name.toLowerCase().includes('dzieci'));
  const childDinner = dinner.filter((meal) => meal.name.toLowerCase().includes('dzieci'));
  const [isDragging, setIsDragging] = useState<IMeal>();
  const [activeTab, setActiveTab] = useState<number>((new Date().getDay() + 6) % 7);
  const [addingMealDialog, setAddingMealDialog] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setIsDragging(undefined);
    if (over) {
      setMenu((prev) =>
        prev.map((meal) => {
          if (meal.id !== active.id) return meal;
          if (Object.values(WeekDaysEnum).includes(over.id as WeekDaysEnum)) {
            return {
              ...meal,
              weekday: over.id as WeekDaysEnum,
            };
          }

          if (["breakfast", "lunch", "dinner", "snack"].includes(over.id as string)) {
            return {
              ...meal,
              type: over.id as IMeal['type'],
            };
          }
          return meal
        })
      );
    }
  };

  const clearMenu = () => {
    setMenu([])
    localStorage.removeItem("menu");
  }

  useEffect(() => {
    localStorage.setItem("menu", JSON.stringify(menu));
  }, [menu]);

  useEffect(() => {
    if (cart.length) {
      localStorage.setItem("menu", JSON.stringify(cart));
    }
  }, [cart]);

  useEffect(() => {
    const mealWithQuantityNull = menu.find((meal) => meal.quantity === 0);
    if (mealWithQuantityNull) {
      setMenu((meals) => meals.filter((meal) => meal.quantity !== 0));
    }
  }, [menu, setMenu])

  const handleTabChange = (_event: SyntheticEvent<Element, Event>, value: number) => setActiveTab(value);

  const Meals = ({ weekday }: { weekday: WeekDaysEnum }) => {
    const filterMeals = (meals: IMeal[]) => meals.filter((meal) =>
      (meal.weekday && Object.values(WeekDaysEnum).includes(meal.weekday))
        ? meal.weekday === weekday
        : weekday === WeekDaysEnum.monday) as (IMeal & { id: string })[]

    return (
      <>
        {filterMeals(breakfast).length ? <p>Śniadania</p> : null}
        <Droppable id={`${weekday}:breakfast`} meals={filterMeals(breakfast)} setMenu={setMenu} />
        {filterMeals(lunch).length ? <p>Obiady</p> : null}
        <Droppable id={`${weekday}:lunch`} meals={filterMeals(lunch)} setMenu={setMenu} />
        {filterMeals(adultDinner).length ? <p>Kolacje</p> : null}
        <Droppable id={`${weekday}:dinner`} meals={filterMeals(adultDinner)} setMenu={setMenu} />
        {filterMeals(childDinner).length ? <p>Kolacje dzieci</p> : null}
        <Droppable id={`${weekday}:dinner`} meals={filterMeals(childDinner)} setMenu={setMenu} />
        {filterMeals(snack).length ? <p>Przekąski</p> : null}
        <Droppable id={`${weekday}:snack`} meals={filterMeals(snack)} setMenu={setMenu} />
      </>
    )
  };

  return (
    <Box sx={{ px: 1, paddingTop: 8 }}>
      {!menu.length ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: "center", flexDirection: "column", paddingTop: 16 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-shopping-bag w-16 h-16 text-gray-300 mb-4" data-fg-cxtk1="5.12:5.5215:/components/Cart.tsx:29:9:808:56:e:ShoppingBag::::::sRE"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
          <Typography sx={{ marginBottom: 1 }} color="#6a7282">Twója lista posiłków jest pusta</Typography>
          <Typography variant="body2" color="#99a1af">Dodaj posiłki z listy aby rozpocząć</Typography>
        </Box>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={({ active }) => {
              const meal = menu.find((m) => m.id === active.id);
              if (meal) setIsDragging(meal);
            }}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setIsDragging(undefined)}
          >
            <div>
              {isDragging && <WeekdayOverlay />}
              <Paper sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1 }} elevation={3}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', background: "white" }}>
                  <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons={false}>
                    <Tab sx={{ minWidth: 55 }} label="Pn" />
                    <Tab sx={{ minWidth: 55 }} label="Wt" />
                    <Tab sx={{ minWidth: 55 }} label="Śr" />
                    <Tab sx={{ minWidth: 55 }} label="Czw" />
                    <Tab sx={{ minWidth: 55 }} label="Pt" />
                    <Tab sx={{ minWidth: 55 }} label="Sb" />
                    <Tab sx={{ minWidth: 55 }} label="Nd" />
                  </Tabs>
                </Box>
              </Paper>
              {activeTab === 0 && <Meals weekday={WeekDaysEnum.monday} />}
              {activeTab === 1 && <Meals weekday={WeekDaysEnum.tuesday} />}
              {activeTab === 2 && <Meals weekday={WeekDaysEnum.wednesday} />}
              {activeTab === 3 && <Meals weekday={WeekDaysEnum.thursday} />}
              {activeTab === 4 && <Meals weekday={WeekDaysEnum.friday} />}
              {activeTab === 5 && <Meals weekday={WeekDaysEnum.saturday} />}
              {activeTab === 6 && <Meals weekday={WeekDaysEnum.sunday} />}
              <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
                {isDragging ? <img
                  src={isDragging.imageUrl}
                  alt={isDragging.name}
                  className="object-cover rounded-lg flex-shrink-0"
                  style={{ width: 55, height: 55 }}
                /> : null}
              </DragOverlay>
            </div>
          </DndContext>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Button disabled={!menu.length} onClick={clearMenu} variant="text">Wyczyść listę posiłków</Button>
          </div>
        </>
      )}

      <FloatingButton position={{ y: 665, x: 325 }} backgroundColor="#1f8038" color="#fff" onClick={() => setAddingMealDialog(true)}><AddIcon /></FloatingButton>
      <MealListModal
        isOpen={addingMealDialog}
        handleClose={() => setAddingMealDialog(false)}
        handleOpen={() => setAddingMealDialog(true)}
        setMenu={setMenu}
        activeTab={activeTab}
      />
    </Box>
  )
}
